import {
    CommunityFaq,
    CommunityFee,
    CommunityOfficeHours,
    CommunityProfile,
} from '../../../../models/Company/types'
import {
    COMMUNITY_WEEKDAYS,
    emptyOfficeHours,
    officeHoursAreSet,
} from '../../../../hooks/useCommunityProfile'

export const FACT_FIELDS: { key: ScalarKey; label: string }[] = [
    { key: 'amenities', label: 'Amenities' },
    { key: 'parking', label: 'Parking' },
    { key: 'laundry', label: 'Laundry' },
    { key: 'utilities_included', label: 'Utilities included' },
    { key: 'income_requirements', label: 'Income requirements' },
    { key: 'security_deposit', label: 'Security deposit' },
    { key: 'insurance_notes', label: 'Insurance' },
]

export const MORE_FIELDS: { key: ScalarKey; label: string }[] = [
    { key: 'furnished', label: 'Furnished' },
    { key: 'lease_style', label: 'Lease style' },
    { key: 'roommate_matching', label: 'Roommate matching' },
    { key: 'students_required', label: 'Students required' },
    { key: 'pet_animals', label: 'Allowed animals' },
    { key: 'pet_max', label: 'Max pets per unit' },
    { key: 'pet_notes', label: 'Pet notes' },
]

export type ScalarKey =
    | 'amenities'
    | 'parking'
    | 'laundry'
    | 'utilities_included'
    | 'income_requirements'
    | 'security_deposit'
    | 'insurance_notes'
    | 'furnished'
    | 'lease_style'
    | 'roommate_matching'
    | 'students_required'
    | 'pet_animals'
    | 'pet_max'
    | 'pet_notes'

export type SourceChoice = 'current' | 'website'

export interface ScalarPair {
    key: ScalarKey
    label: string
    current: string
    website: string
    type: 'conflict' | 'addition'
}

export interface HoursPair {
    current: CommunityOfficeHours
    website: CommunityOfficeHours
    type: 'conflict' | 'addition'
}

export interface LogoPair {
    currentUrl: string
    websiteUrl: string
    type: 'conflict' | 'addition'
}

export interface ListPair<T> {
    key: string
    type: 'conflict' | 'addition' | 'keep'
    current?: T
    website?: T
}

export interface FillReview {
    logo: LogoPair | null
    hours: HoursPair | null
    facts: ScalarPair[]
    more: ScalarPair[]
    fees: ListPair<CommunityFee>[]
    faqs: ListPair<CommunityFaq>[]
}

export type FillStepId =
    | 'summary'
    | 'logo'
    | 'hours'
    | 'facts'
    | 'more'
    | 'faqs'

const ANIMAL_LABELS: Record<string, string> = {
    dog: 'Dog',
    cat: 'Cat',
    fish: 'Fish',
    reptile: 'Reptile',
    rodent: 'Rodent',
    bird: 'Bird',
}

const FURNISHED_LABELS: Record<string, string> = {
    yes: 'Furnished',
    no: 'Unfurnished',
    partial: 'Partially furnished',
}

const LEASE_LABELS: Record<string, string> = {
    individual: 'Individual / by the bedroom',
    joint: 'Joint lease',
}

export function textValue(value?: string | null): string {
    return (value || '').trim()
}

export function isEmptyText(value?: string | null): boolean {
    return !textValue(value)
}

export function formatTime(hhmm: string): string {
    const match = /^(\d{1,2}):(\d{2})/.exec(hhmm || '')
    if (!match) return hhmm
    const hour24 = Number(match[1])
    const minutes = match[2]
    const suffix = hour24 >= 12 ? 'PM' : 'AM'
    const hour = ((hour24 + 11) % 12) + 1
    return `${hour}:${minutes} ${suffix}`
}

export function formatHoursBlock(hours?: CommunityOfficeHours | null): string {
    const value = hours || emptyOfficeHours()
    return COMMUNITY_WEEKDAYS.map((day) => {
        const label = day.charAt(0).toUpperCase() + day.slice(1)
        const row = value[day]
        if (row?.closed || !row?.start || !row?.end) {
            return `${label}: Closed`
        }
        return `${label}: ${formatTime(row.start)} – ${formatTime(row.end)}`
    }).join('\n')
}

export function hoursAreEqual(
    left?: CommunityOfficeHours | null,
    right?: CommunityOfficeHours | null,
): boolean {
    return formatHoursBlock(left) === formatHoursBlock(right)
}

function formatTriState(value: boolean | null | undefined): string {
    if (value === true) return 'Yes'
    if (value === false) return 'No'
    return ''
}

function formatAnimals(types?: string[]): string {
    return (types || [])
        .map((id) => ANIMAL_LABELS[id] || id)
        .filter(Boolean)
        .join(', ')
}

export function scalarDisplay(
    profile: Partial<CommunityProfile> | undefined,
    key: ScalarKey,
): string {
    if (!profile) return ''
    if (key === 'furnished') {
        const value = profile.furnished
        if (!value || value === 'unknown') return ''
        return FURNISHED_LABELS[value] || value
    }
    if (key === 'lease_style') {
        const value = profile.lease_style
        if (!value || value === 'unknown') return ''
        return LEASE_LABELS[value] || value
    }
    if (key === 'roommate_matching') {
        return formatTriState(profile.roommate_matching)
    }
    if (key === 'students_required') {
        return formatTriState(profile.students_required)
    }
    if (key === 'pet_animals') {
        return formatAnimals(profile.pet_defaults?.allowed_animal_types)
    }
    if (key === 'pet_max') {
        return textValue(profile.pet_defaults?.max_pets_per_unit)
    }
    return textValue(profile[key] as string | undefined)
}

function collectPairs(
    current: CommunityProfile,
    proposal: Partial<CommunityProfile>,
    fields: { key: ScalarKey; label: string }[],
): ScalarPair[] {
    const pairs: ScalarPair[] = []
    fields.forEach(({ key, label }) => {
        const currentValue = scalarDisplay(current, key)
        const websiteValue = scalarDisplay(proposal, key)
        if (!websiteValue) return
        if (!currentValue) {
            pairs.push({
                key,
                label,
                current: currentValue,
                website: websiteValue,
                type: 'addition',
            })
            return
        }
        if (currentValue !== websiteValue) {
            pairs.push({
                key,
                label,
                current: currentValue,
                website: websiteValue,
                type: 'conflict',
            })
        }
    })
    return pairs
}

function feeKey(fee: CommunityFee): string {
    return textValue(fee.name).toLowerCase()
}

function faqKey(faq: CommunityFaq): string {
    return textValue(faq.question).toLowerCase()
}

function feeBody(fee: CommunityFee): string {
    return [fee.amount, fee.covers].map((part) => textValue(part)).join('\n')
}

function faqBody(faq: CommunityFaq): string {
    return textValue(faq.answer)
}

export function formatFee(fee: CommunityFee): string {
    const amount = textValue(fee.amount) || 'Amount not listed'
    const covers = textValue(fee.covers)
    return covers
        ? `${fee.name}: ${amount} — ${covers}`
        : `${fee.name}: ${amount}`
}

export function formatFaq(faq: CommunityFaq): string {
    return `Q: ${faq.question}\nA: ${faq.answer}`
}

function collectListPairs<T>(
    currentItems: T[],
    websiteItems: T[],
    keyOf: (item: T) => string,
    bodyOf: (item: T) => string,
): ListPair<T>[] {
    const currentByKey = new Map<string, T>()
    currentItems.forEach((item) => {
        const key = keyOf(item)
        if (key) currentByKey.set(key, item)
    })
    const websiteByKey = new Map<string, T>()
    websiteItems.forEach((item) => {
        const key = keyOf(item)
        if (key) websiteByKey.set(key, item)
    })
    const keys = Array.from(
        new Set(
            Array.from(currentByKey.keys()).concat(
                Array.from(websiteByKey.keys()),
            ),
        ),
    )
    const pairs: ListPair<T>[] = []
    keys.forEach((key) => {
        const current = currentByKey.get(key)
        const website = websiteByKey.get(key)
        if (current && website) {
            if (bodyOf(current) !== bodyOf(website)) {
                pairs.push({ key, type: 'conflict', current, website })
            }
            return
        }
        if (website && !current) {
            pairs.push({ key, type: 'addition', website })
            return
        }
        if (current && !website) {
            pairs.push({ key, type: 'keep', current })
        }
    })
    return pairs
}

export function buildFillReview(
    current: CommunityProfile,
    proposal: Partial<CommunityProfile>,
    extractedLogoUrl?: string,
): FillReview {
    const websiteHours = proposal.office_hours
    let hours: HoursPair | null = null
    if (officeHoursAreSet(websiteHours)) {
        if (!officeHoursAreSet(current.office_hours)) {
            hours = {
                current: current.office_hours || emptyOfficeHours(),
                website: websiteHours as CommunityOfficeHours,
                type: 'addition',
            }
        } else if (!hoursAreEqual(current.office_hours, websiteHours)) {
            hours = {
                current: current.office_hours,
                website: websiteHours as CommunityOfficeHours,
                type: 'conflict',
            }
        }
    }

    const currentLogo = textValue(current.profile_picture)
    const websiteLogo = textValue(extractedLogoUrl || proposal.logo_url)
    let logo: LogoPair | null = null
    if (websiteLogo) {
        if (!currentLogo) {
            logo = {
                currentUrl: currentLogo,
                websiteUrl: websiteLogo,
                type: 'addition',
            }
        } else if (currentLogo !== websiteLogo) {
            logo = {
                currentUrl: currentLogo,
                websiteUrl: websiteLogo,
                type: 'conflict',
            }
        }
    }

    return {
        logo,
        hours,
        facts: collectPairs(current, proposal, FACT_FIELDS),
        more: collectPairs(current, proposal, MORE_FIELDS),
        fees: collectListPairs(
            current.extra_fees || [],
            proposal.extra_fees || [],
            feeKey,
            feeBody,
        ),
        faqs: collectListPairs(
            current.faqs || [],
            proposal.faqs || [],
            faqKey,
            faqBody,
        ),
    }
}

export function reviewHasConflicts(review: FillReview): boolean {
    if (review.logo?.type === 'conflict') return true
    if (review.hours?.type === 'conflict') return true
    if (review.facts.some((item) => item.type === 'conflict')) return true
    if (review.more.some((item) => item.type === 'conflict')) return true
    if (review.fees.some((item) => item.type === 'conflict')) return true
    if (review.faqs.some((item) => item.type === 'conflict')) return true
    return false
}

export function reviewHasWork(review: FillReview): boolean {
    return (
        !!review.logo ||
        !!review.hours ||
        review.facts.length > 0 ||
        review.more.length > 0 ||
        review.fees.some((item) => item.type !== 'keep') ||
        review.faqs.some((item) => item.type !== 'keep')
    )
}

export function fillReviewSteps(review: FillReview): FillStepId[] {
    if (!reviewHasWork(review)) return []
    if (!reviewHasConflicts(review)) return ['summary']
    const steps: FillStepId[] = []
    if (review.logo) steps.push('logo')
    if (review.hours) steps.push('hours')
    if (review.facts.length) steps.push('facts')
    if (review.more.length || review.fees.length) steps.push('more')
    if (review.faqs.length) steps.push('faqs')
    return steps
}

export interface ListDecision {
    choice?: SourceChoice
    include: boolean
}

export interface FillDecisions {
    logo?: SourceChoice
    hours?: SourceChoice
    scalars: Partial<Record<ScalarKey, SourceChoice>>
    fees: Record<string, ListDecision>
    faqs: Record<string, ListDecision>
}

export function initialFillDecisions(review: FillReview): FillDecisions {
    const fees: Record<string, ListDecision> = {}
    review.fees.forEach((item) => {
        fees[item.key] = { include: true }
    })
    const faqs: Record<string, ListDecision> = {}
    review.faqs.forEach((item) => {
        faqs[item.key] = { include: true }
    })
    return { scalars: {}, fees, faqs }
}

export function stepConflictsResolved(
    step: FillStepId,
    review: FillReview,
    decisions: FillDecisions,
): boolean {
    if (step === 'summary') return true
    if (step === 'logo') {
        return review.logo?.type !== 'conflict' || !!decisions.logo
    }
    if (step === 'hours') {
        return review.hours?.type !== 'conflict' || !!decisions.hours
    }
    const scalars = step === 'facts' ? review.facts : review.more
    const unresolvedScalar = scalars.some(
        (item) => item.type === 'conflict' && !decisions.scalars[item.key],
    )
    if (unresolvedScalar) return false
    if (step === 'more') {
        return !review.fees.some(
            (item) =>
                item.type === 'conflict' && !decisions.fees[item.key]?.choice,
        )
    }
    if (step === 'faqs') {
        return !review.faqs.some(
            (item) =>
                item.type === 'conflict' && !decisions.faqs[item.key]?.choice,
        )
    }
    return true
}

function applyScalarToProfile(
    profile: CommunityProfile,
    proposal: Partial<CommunityProfile>,
    pair: ScalarPair,
    choice?: SourceChoice,
): CommunityProfile {
    const useWebsite = pair.type === 'addition' || choice === 'website'
    const source = useWebsite ? proposal : profile
    if (pair.key === 'furnished') {
        return {
            ...profile,
            furnished: useWebsite
                ? proposal.furnished || 'unknown'
                : profile.furnished,
        }
    }
    if (pair.key === 'lease_style') {
        return {
            ...profile,
            lease_style: useWebsite
                ? proposal.lease_style || 'unknown'
                : profile.lease_style,
        }
    }
    if (pair.key === 'roommate_matching') {
        return {
            ...profile,
            roommate_matching: useWebsite
                ? proposal.roommate_matching ?? null
                : profile.roommate_matching,
        }
    }
    if (pair.key === 'students_required') {
        return {
            ...profile,
            students_required: useWebsite
                ? proposal.students_required ?? null
                : profile.students_required,
        }
    }
    if (pair.key === 'pet_animals' || pair.key === 'pet_max') {
        return {
            ...profile,
            pet_defaults: {
                ...profile.pet_defaults,
                allowed_animal_types:
                    pair.key === 'pet_animals'
                        ? useWebsite
                            ? proposal.pet_defaults?.allowed_animal_types || []
                            : profile.pet_defaults.allowed_animal_types
                        : profile.pet_defaults.allowed_animal_types,
                max_pets_per_unit:
                    pair.key === 'pet_max'
                        ? useWebsite
                            ? proposal.pet_defaults?.max_pets_per_unit || ''
                            : profile.pet_defaults.max_pets_per_unit
                        : profile.pet_defaults.max_pets_per_unit,
            },
        }
    }
    if (pair.key === 'pet_notes') {
        return {
            ...profile,
            pet_notes: useWebsite ? source.pet_notes || '' : profile.pet_notes,
        }
    }
    return {
        ...profile,
        [pair.key]: useWebsite
            ? (proposal[pair.key] as string) || ''
            : (profile[pair.key] as string) || '',
    }
}

function resolveList<T>(
    items: ListPair<T>[],
    decisions: Record<string, ListDecision>,
): T[] {
    const resolved: T[] = []
    items.forEach((item) => {
        const decision = decisions[item.key] || { include: true }
        if (item.type === 'keep') {
            if (decision.include !== false && item.current) {
                resolved.push(item.current)
            }
            return
        }
        if (item.type === 'addition') {
            if (decision.include !== false && item.website) {
                resolved.push(item.website)
            }
            return
        }
        if (decision.choice === 'website' && item.website) {
            resolved.push(item.website)
            return
        }
        if (item.current) {
            resolved.push(item.current)
        }
    })
    return resolved
}

export function resolveFilledProfile(
    current: CommunityProfile,
    proposal: Partial<CommunityProfile>,
    review: FillReview,
    decisions: FillDecisions,
): { profile: CommunityProfile; applyLogo: boolean; logoUrl: string } {
    let next: CommunityProfile = { ...current }
    ;[...review.facts, ...review.more].forEach((pair) => {
        next = applyScalarToProfile(
            next,
            proposal,
            pair,
            decisions.scalars[pair.key],
        )
    })
    if (review.hours) {
        const useWebsite =
            review.hours.type === 'addition' || decisions.hours === 'website'
        next = {
            ...next,
            office_hours: useWebsite
                ? review.hours.website
                : review.hours.current,
        }
    }
    next = {
        ...next,
        extra_fees: resolveList(review.fees, decisions.fees),
        faqs: resolveList(review.faqs, decisions.faqs),
    }
    const applyLogo =
        !!review.logo &&
        (review.logo.type === 'addition' || decisions.logo === 'website')
    return {
        profile: next,
        applyLogo,
        logoUrl: applyLogo ? review.logo?.websiteUrl || '' : '',
    }
}

export function formatLastFilledAt(iso: string): string {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    const diffMs = Date.now() - date.getTime()
    const mins = Math.floor(diffMs / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days === 1) return '1 day ago'
    if (days < 30) return `${days} days ago`
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}
